import { useState } from 'react';

import { CheckboxGroup, onEnterKeyPress } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  CareerQuestion,
  CareerUserResponseAnswer,
  CareerUserResponseSet,
} from '@backrs_ui/grow-ui/src/models/models';
import { resolveNextStepsSelection } from '@backrs_ui/grow-ui/src/pages/Survey/survey.util';

import { Box, SxProps, Theme, Button } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';

import { QuestionText } from '../atoms/QuestionText';

const BUTTON_STRINGS = appStrings.common.button;

export type FinalQuestionModalProps = Readonly<{
  question: CareerQuestion;
  responsesForQuestion?: CareerUserResponseAnswer;
  userResponseSet: CareerUserResponseSet;
  onBack: () => void;
  onSaveAndContinue: (finalQuestionResponse: CareerUserResponseAnswer) => void;
}>;

export function FinalQuestionModal({
  question,
  responsesForQuestion,
  userResponseSet,
  onBack,
  onSaveAndContinue,
}: FinalQuestionModalProps) {
  const [selectedValues, setSelectedValues] = useState<string[]>(
    responsesForQuestion?.career_question_option_ids.map((id) => id.toString()) || [],
  );

  const isSaveDisabled = selectedValues.length === 0;

  const handleSaveAndContinue = () => {
    if (isSaveDisabled) {
      return;
    }

    const optionIds = selectedValues.map((value) => parseInt(value));

    const finalResponse: CareerUserResponseAnswer = responsesForQuestion
      ? {
          ...responsesForQuestion,
          career_question_option_ids: optionIds,
        }
      : {
          id: uuidv4(),
          career_user_response_set_id: userResponseSet.id,
          career_question_id: question.id,
          career_question_option_ids: optionIds,
        };

    onSaveAndContinue(finalResponse);
  };

  const { title, question: question_body } = question;
  const options = question.options.filter((option) => option.is_active !== false);

  return (
    <Box
      sx={overlaySx}
      tabIndex={0}
      onKeyDown={(event) => onEnterKeyPress(event, handleSaveAndContinue)}
    >
      <Box sx={modalCardSx}>
        <QuestionText hasFacts={false} title={title} question_body={question_body} />

        <Box sx={optionsContainerSx}>
          <CheckboxGroup
            containerSx={checkboxGroupSx}
            options={options.map((option) => ({
              label: option.answer,
              value: option.id.toString(),
              disabled: false,
            }))}
            selectedValues={selectedValues}
            maxSelectedOptions={options.length}
            setSelectedValues={(next) =>
              setSelectedValues((prev) =>
                resolveNextStepsSelection(
                  next.map((v) => parseInt(v)),
                  prev.map((v) => parseInt(v)),
                  options,
                ).map(String),
              )
            }
          />
        </Box>

        <Box sx={buttonContainerSx}>
          <Button
            variant="outlined"
            size="md"
            sx={backButtonSx}
            onClick={onBack}
            id="survey-final-back-button"
          >
            {BUTTON_STRINGS.back}
          </Button>
          <Button
            variant="contained"
            hierarchy="Primary"
            size="md"
            sx={saveButtonSx}
            onClick={handleSaveAndContinue}
            disabled={isSaveDisabled}
            id="survey-final-save-button"
          >
            {BUTTON_STRINGS.saveAndContinue}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

const overlaySx: SxProps<Theme> = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.46)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
  height: '100%',
};

const modalCardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: backrs.colors['Colors/Base/White'],
  width: '600px',
  maxWidth: '600px',
  flexShrink: 1,
  minWidth: 0,
  borderRadius: '1rem',
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  zIndex: 1,
  animation: 'fadeInUp 0.4s ease-out',
  '@keyframes fadeInUp': {
    '0%': {
      opacity: 0,
      transform: 'translateY(20px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
  '@media (max-width: 600px)': {
    width: '100%',
    maxWidth: '100%',
    gap: backrs.spacing['spacing-3xl'],
  },
});

const optionsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  padding: backrs.spacing['spacing-3xl'],
  '@media (max-width: 600px)': {
    gridTemplateColumns: '1fr',
    maxHeight: '300px',
    overflowY: 'auto',
    padding: backrs.spacing['spacing-xxs'],
  },
});

const checkboxGroupSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  pb: backrs.spacing['spacing-5xl'],
  zIndex: 999,
  position: 'relative',
});

const buttonContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-lg'],
  padding: backrs.spacing['spacing-3xl'],
  pt: 0,
});

const backButtonSx: SxProps<Theme> = ({ backrs }) => ({
  borderColor: backrs.colors['Colors/Border/border-primary'],
  color: backrs.colors['Colors/Base/Black'],
  backgroundColor: backrs.colors['Colors/Base/White'],
  borderRadius: backrs.radius['radius-md'],
  textTransform: 'none',
  fontWeight: 500,
  '&:hover': {
    borderColor: backrs.colors['Colors/Border/border-primary'],
    backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
  },
  width: '100%',
});

const saveButtonSx: SxProps<Theme> = ({ backrs }) => ({
  // minWidth: 'fit-content',
  width: '100%',
  '@media (max-width: 600px)': {
    '&:hover': {
      boxShadow: 'none',
      transform: 'none',
      backgroundColor: backrs.colors['Colors/Background/bg-disabled'],
      color: backrs.colors['Colors/Foreground/fg-disabled'],
    },
  },
  WebkitTapHighlightColor: 'transparent',
});
